import { FairQueueStats } from '@activepieces/shared';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTitle } from '@/components/ui/table-title';
import { adminApi } from '@/features/internal-admin/lib/admin-api';

const REFRESH_INTERVAL_MS = 3000;

// Human-readable names for the internal queue keys.
const queueLabel = (queue: string): string => {
  const labels: Record<string, string> = {
    oneTimeJobs: t('Flow executions'),
    webhookJobs: t('Webhooks'),
  };
  return labels[queue] ?? queue;
};

type TenantRow = FairQueueStats['tenants'][number];

// One displayed row = one (tenant, queue) pair with something to show.
type QueueRow = {
  tenant: TenantRow;
  // null → the tenant is being inspected but has no per-queue activity.
  queue: string | null;
  waiting: number;
  running: number;
};

// Flattens tenants into (tenant, queue) rows, keeping only queues where the
// tenant has activity (waiting or running). A tenant with no activity at all
// (e.g. an inspected tenant that is idle) still yields one placeholder row so
// it stays visible.
const toQueueRows = (tenants: TenantRow[], queueNames: string[]): QueueRow[] =>
  tenants.flatMap((tenant): QueueRow[] => {
    const rows: QueueRow[] = queueNames
      .map((queue) => ({
        tenant,
        queue,
        waiting: tenant.pendingJobsByQueue[queue] ?? 0,
        running: tenant.activeJobsByQueue[queue] ?? 0,
      }))
      .filter((row) => row.waiting > 0 || row.running > 0);
    if (rows.length > 0) {
      return rows;
    }
    return [{ tenant, queue: null, waiting: 0, running: 0 }];
  });

// "Throttled" must mean throttled *right now*, not ever: rejectedJobs is a
// cumulative counter (bounded only by a 7-day sliding TTL), so
// `rejectedJobs > 0` would stick for days after the last rejection.
// We flag it only while the rejection count is still climbing (delta > 0
// between two polls); otherwise the row falls back to Running/Idle.
// Throttling is tracked per tenant (cross-queue), so the badge means "rejected
// on at least one queue" and applies to every row of that tenant.
const rowStatus = (
  activelyThrottled: boolean,
  running: boolean,
): { label: string; variant: 'destructive' | 'default' | 'outline' } => {
  if (activelyThrottled) {
    return { label: t('Throttled'), variant: 'destructive' };
  }
  if (running) {
    return { label: t('Running'), variant: 'default' };
  }
  return { label: t('Idle'), variant: 'outline' };
};

const AdminFairQueuePage = () => {
  const [inputValue, setInputValue] = useState('');
  // The tenant currently inspected. Empty = real-time view of contending tenants.
  const [inspectedTenantId, setInspectedTenantId] = useState('');
  const isInspecting = inspectedTenantId.length > 0;

  const { data, isLoading, isError } = useQuery<FairQueueStats, Error>({
    queryKey: ['admin-fair-queue-stats', inspectedTenantId],
    queryFn: () =>
      adminApi.getFairQueueStats(
        isInspecting ? [inspectedTenantId] : undefined,
      ),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  // A tenant is "actively throttled" only while its rejection count keeps
  // climbing between polls — not forever after its first-ever rejection.
  const prevRejectedRef = useRef<Record<string, number>>({});
  const [throttledIds, setThrottledIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!data) {
      return;
    }
    const next: Record<string, boolean> = {};
    for (const tenant of data.tenants) {
      const prev = prevRejectedRef.current[tenant.tenantId];
      next[tenant.tenantId] = prev !== undefined && tenant.rejectedJobs > prev;
      prevRejectedRef.current[tenant.tenantId] = tenant.rejectedJobs;
    }
    setThrottledIds(next);
  }, [data]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setInspectedTenantId(inputValue.trim());
  };

  const clearSearch = () => {
    setInputValue('');
    setInspectedTenantId('');
  };

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">
          {t('Failed to load fair queue stats')}
        </p>
      </div>
    );
  }

  const queues = data?.global.queues ?? {};
  const queueNames = Object.keys(queues);
  const queueRows = toQueueRows(data?.tenants ?? [], queueNames);

  return (
    <div className="flex flex-col w-full gap-6">
      <div className="flex flex-col gap-1">
        <TableTitle>{t('Fair Queue')}</TableTitle>
        <p className="text-sm text-muted-foreground max-w-3xl">
          {t(
            'Real-time view of fairness between tenants. When several tenants compete for the queue, each is limited to a share of the workers. A tenant only appears here while it has jobs waiting for a free worker — a tenant running below capacity creates no contention.',
          )}
        </p>
      </div>

      <form onSubmit={submitSearch} className="flex items-center gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t('Inspect a tenant by ID…')}
          className="max-w-xs"
        />
        <Button type="submit" variant="outline" size="icon">
          <Search className="size-4" />
        </Button>
        {isInspecting && (
          <Button type="button" variant="ghost" size="sm" onClick={clearSearch}>
            <X className="size-4 mr-1" />
            {t('Back to live view')}
          </Button>
        )}
      </form>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('Machines online')}</CardDescription>
              <CardTitle className="text-3xl">
                {data?.global.machinesOnline ?? 0}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              {t(
                'Worker instances heartbeating (last 60s). Multiplies the budget.',
              )}
            </CardContent>
          </Card>
          {queueNames.map((queue) => {
            const q = queues[queue];
            return (
              <Card key={queue}>
                <CardHeader className="pb-2">
                  <CardDescription>
                    {t('Fair share')} · {queueLabel(queue)}
                  </CardDescription>
                  <CardTitle className="text-3xl">
                    {q.activeTenants === 0 && (
                      <span className="text-sm font-normal text-muted-foreground mr-1.5">
                        {t('up to')}
                      </span>
                    )}
                    {q.fairShare}
                    <span className="text-sm font-normal text-muted-foreground">
                      {' '}
                      {t('/ tenant')}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground flex flex-col gap-1">
                  <div className="flex justify-between">
                    <span>{t('Tenants competing')}</span>
                    <span className="font-medium text-foreground tabular-nums">
                      {q.activeTenants}
                    </span>
                  </div>
                  {/* Reserved (accounting) above running (real BullMQ jobs)
                      means leaked reservations are throttling tenants */}
                  <div className="flex justify-between">
                    <span>{t('Reserved slots')}</span>
                    <span
                      className={
                        q.reservedSlots > q.busyWorkers
                          ? 'text-destructive font-medium tabular-nums'
                          : 'font-medium text-foreground tabular-nums'
                      }
                    >
                      {q.reservedSlots}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('Jobs waiting')}</span>
                    <span className="font-medium text-foreground tabular-nums">
                      {q.waitingJobs}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('Jobs running')}</span>
                    <span className="font-medium text-foreground tabular-nums">
                      {q.busyWorkers} / {q.maxWorkers} {t('workers')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Tenant')}</TableHead>
              <TableHead>{t('Queue')}</TableHead>
              <TableHead>{t('Waiting')}</TableHead>
              <TableHead>{t('Running now')}</TableHead>
              <TableHead>{t('Status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ) : queueRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center text-muted-foreground"
                >
                  {isInspecting
                    ? t('No stats for this tenant')
                    : t('No tenants competing right now')}
                </TableCell>
              </TableRow>
            ) : (
              queueRows.map((row) => {
                const status = rowStatus(
                  throttledIds[row.tenant.tenantId] ?? false,
                  row.running > 0,
                );
                return (
                  <TableRow key={`${row.tenant.tenantId}:${row.queue ?? '—'}`}>
                    <TableCell className="font-medium">
                      {row.tenant.tenantId}
                    </TableCell>
                    <TableCell>
                      {row.queue ? queueLabel(row.queue) : '—'}
                    </TableCell>
                    <TableCell>{row.waiting}</TableCell>
                    <TableCell>{row.running}</TableCell>
                    <TableCell>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <div className="text-xs text-muted-foreground flex flex-col gap-1">
          <span>
            {t(
              'One row per tenant and queue. Running now is directly comparable to that queue’s fair share above.',
            )}
          </span>
          <span>
            <strong>{t('Waiting')}</strong>:{' '}
            {t(
              'jobs queued for this tenant on this queue, waiting for a free worker.',
            )}
          </span>
          <span>
            <strong>{t('Running now')}</strong>:{' '}
            {t('jobs currently executing for this tenant on this queue.')}
          </span>
          <span>
            <strong>{t('Status')}</strong>: <strong>{t('Throttled')}</strong> ={' '}
            {t('being rejected on at least one queue right now')} ·{' '}
            <strong>{t('Running')}</strong> = {t('executing on this queue')} ·{' '}
            <strong>{t('Idle')}</strong> = {t('no active job on this queue')}.
          </span>
          <span>
            <strong>{t('Reserved slots')}</strong>:{' '}
            {t(
              'worker slots held by the fairness accounting. Persistently above Jobs running = leaked reservations throttling the tenant (self-heals via deadlines and the periodic reconciliation).',
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdminFairQueuePage;
